/**
 * «این کدام گوشی/مرورگر است؟» — یک شناسه‌ی تصادفی که روی همان دستگاه می‌ماند.
 *
 * ═══ چرا لازم شد ═══
 *
 * سوال‌هایی که جواب نداشتند: این آدم چند مرورگر دارد؟ آخرینش کدام بود؟ چند
 * تایشان اجازه‌ی اعلان داده‌اند؟
 *
 * هیچ‌کدام از چیزهای موجود جواب نمی‌دادند. `dev_log.session_id` هر بار باز
 * شدنِ اپ عوض می‌شود، پس دو بازدید از یک مرورگر به هم وصل نمی‌شوند. و
 * `push_tokens` فقط سطرِ کسانی را دارد که اجازه **داده‌اند** — یعنی مخرجِ
 * کسر همیشه گم است.
 *
 * ═══ چرا تصادفی و نه ساخته‌شده از خودِ دستگاه ═══
 *
 * fingerprintِ مرورگر (اندازه‌ی صفحه، فونت‌ها، …) بینِ حساب‌ها و حتی بینِ
 * سایت‌ها قابلِ ردیابی است. سوالِ ما «چند مرورگر» است نه «کدام آدم»، و یک
 * عددِ تصادفی همان‌قدر خوب جواب می‌دهد بدونِ اینکه چیزی درباره‌ی دستگاه
 * بگوید.
 *
 * ⚠ عارضه‌اش: پاک کردنِ حافظه‌ی مرورگر شناسه را از بین می‌برد و دفعه‌ی بعد
 * یک «مرورگرِ تازه» شمرده می‌شود. عمداً پذیرفته شده — جایگزینش همان
 * ردیابیِ سخت‌افزاری بود.
 */
import { storage } from '../platform.ts';

const KEY = 'whosup.deviceId.v1';

let cached: string | null = null;

/** همان مولدِ `analyticsQueue.newSessionId` — رمزنگارانه لازم نیست باشد. */
function mint(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // بعضی محیط‌های ری‌اکت‌نیتیو و مرورگرهای قدیمی randomUUID ندارند.
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * شناسه‌ی این دستگاه؛ اگر نبود، ساخته و ذخیره می‌شود.
 *
 * `null` وقتی حافظه اصلاً در دسترس نیست (پنجره‌ی ناشناسِ بعضی مرورگرها حتی
 * خواندن را هم پرت می‌کند). آن‌وقت ثبت انجام نمی‌شود — که بهتر از ساختنِ یک
 * شناسه‌ی تازه در هر بار باز شدن است، چون آن یکی شمارشِ «چند مرورگر» را
 * بی‌معنی می‌کرد.
 */
export async function deviceId(): Promise<string | null> {
  if (cached) return cached;
  try {
    const saved = await storage().getItem(KEY);
    if (saved) { cached = saved; return cached; }
    const fresh = mint();
    await storage().setItem(KEY, fresh);
    cached = fresh;
    return cached;
  } catch {
    return null;
  }
}

/** فقط برای تست — کشِ درون‌حافظه‌ای را خالی می‌کند. */
export const resetDeviceIdCache = (): void => { cached = null; };
